import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { type ChatAction } from '@atlassian/rovo-conversation-actions/types';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { messages } from '@atlassian/rovo-fomo-nudge/i18n';
import { createFomoNudgeAction } from '@atlassian/rovo-fomo-nudge/create-fomo-nudge-action';

/**
 *
 * Phasing (matches the Jira side):
 *   1 — gates compile, builder returns null, no UI rendered.
 *   2 — avatar UI + i18n via shared package.
 *   3 — non-Rovo-MAU TAP trait gate.
 *   4 — collaborator data via TWG / AGG.
 *   5 — localStorage "show once" adapter.
 *
 * NOTE: launch-date scheduling is handled in Statsig (per project decision),
 * not in code — this hook has no notion of "today".
 */
export function useFomoNudgeAction(): ChatAction | null {
	const { formatMessage } = useIntl();

	return useMemo(() => {
		// Killswitch — also checks is_rovo_chat_mau
		if (!fg('rovo_button_collab_teammates_fomo_nudge_enabled')) {
			return null;
		}
		// Treatment lookup. expVal triggers experiment exposure, which we only
		// want when the killswitch is actually on.
		if (!expVal('rovo_button_collab_teammates_fomo_nudge', 'isEnabled', false)) {
			return null;
		}

		// TODO(Phase 5): bail when localStorage says we've already shown it.

		return createFomoNudgeAction({
			collaborators: [], // TODO(Phase 4): supply real collaborators via TWG / AGG.
			title: formatMessage(messages.fomoNudgeTitle),
			description: formatMessage(messages.fomoNudgeDescription),
			// onClick omitted — triggerAction early-returns when onClick is defined,
			// preventing chat from opening. TODO(Phase 5): add for storage/analytics.
		});
	}, [formatMessage]);
}
